if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      /**
       * filterMediaByVariant — hides/shows images based on alt text grouping tags.
       *
       * ALT TEXT FORMAT (set in Shopify Admin → Products → Media → Add alt text):
       *   #color_Red        → show only when option "Color" = "Red"
       *   #color_Dark Blue  → show only when option "Color" = "Dark Blue"
       *   #farbe_Rot        → show only when option "Farbe" = "Rot"  (use your exact option name, lowercase)
       *   (blank / no #)    → always show (shared image shown for all variants)
       *
       * REQUIRES these three Liquid changes (see full instructions below setActiveMedia):
       *   1. product-media-gallery.liquid: add data-option-names on <media-gallery>
       *   2. product-thumbnail.liquid: add data-media-alt on each slide <div>
       *   3. product-media-gallery.liquid: add data-media-alt on each thumb <div>
       */
      filterMediaByVariant(variant) {
        if (!variant) return;

        // ── Read option names from the media-gallery element ────────────────
        // Requires: data-option-names="{{ product.options | join: ',' | downcase }}"
        // on the <media-gallery> tag in product-media-gallery.liquid
        // Example rendered value: "color,size"  or  "farbe,größe"
        const optionNamesRaw = this.dataset.optionNames || '';
        const optionNames = optionNamesRaw
          .split(',')
          .map((n) => n.trim())
          .filter(Boolean);

        // ── Read option values from the variant object ──────────────────────
        // Shopify variant JSON always includes variant.options: ["Red", "Large"]
        // variant.option1 / option2 / option3 are the individual string fallbacks
        const optionValues =
          Array.isArray(variant.options) && variant.options.length
            ? variant.options
            : [variant.option1, variant.option2, variant.option3].filter(
                (v) => v != null
              );

        // ── Build the set of tags this variant matches ──────────────────────
        // Format: "#optionname_optionvalue"  (name is already lowercased from Liquid)
        // Examples: "#color_Red", "#size_Large", "#farbe_Rot"
        const matchedTags = new Set();
        optionNames.forEach((name, i) => {
          if (optionValues[i] != null) {
            matchedTags.add(`#${name}_${optionValues[i]}`);
          }
        });

        // ── Show/hide main slider slides ────────────────────────────────────
        // Requires: data-media-alt="{{ media.alt | escape }}" on each
        // .product__media-item div in product-thumbnail.liquid
        const allSlides = this.querySelectorAll(
          '.product__media-item:not(.swiper-slide-duplicate)'
        );

        allSlides.forEach((slide) => {
          const alt = (slide.dataset.mediaAlt || '').trim();
          // No # prefix → shared image (always visible)
          // Has # prefix → only visible if it matches a variant tag
          const shouldShow = !alt.startsWith('#') || matchedTags.has(alt);
          slide.style.display = shouldShow ? '' : 'none';
        });

        // ── Show/hide thumbnail strip ───────────────────────────────────────
        // Requires: data-media-alt="{{ media.alt | escape }}" on each
        // .product__thumbs-item div in product-media-gallery.liquid
        this.querySelectorAll('.product__thumbs-item').forEach((thumb) => {
          const alt = (thumb.dataset.mediaAlt || '').trim();
          const shouldShow = !alt.startsWith('#') || matchedTags.has(alt);
          thumb.style.display = shouldShow ? '' : 'none';
        });

        // ── Tell Swiper to recalculate now that visibility has changed ──────
        if (this.sliderInstance && this.sliderInstance.slider) {
          this.sliderInstance.slider.update();
        }
        if (this.thumbsInstance && this.thumbsInstance.slider) {
          this.thumbsInstance.slider.update();
        }
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        // 1. Filter images first so hidden slides are removed from Swiper's count
        this.filterMediaByVariant(variant);

        if (this.sliderInstance && this.sliderInstance.slider) {
          const targetMediaId = String(variant.featured_media.id);

          // After filtering, find the visible index of the target slide.
          // We count only original (non-duplicate), non-hidden slides.
          const visibleSlides = [
            ...this.querySelectorAll(
              '.product__media-item:not(.swiper-slide-duplicate)'
            ),
          ].filter((s) => s.style.display !== 'none');

          const visibleIndex = visibleSlides.findIndex(
            (s) => String(s.dataset.mediaId) === targetMediaId
          );

          // slideToLoop(index) uses the real-slide index (not counting duplicates)
          this.sliderInstance.slider.slideToLoop(
            visibleIndex >= 0 ? visibleIndex : 0
          );
        } else {
          this.sortMediaItems(variant);
        }
      }

      /*
       * ═══════════════════════════════════════════════════════════════════
       * REQUIRED LIQUID CHANGES  —  3 files need editing
       * ═══════════════════════════════════════════════════════════════════
       *
       * ── CHANGE A: snippets/product-media-gallery.liquid ─────────────────
       *
       * On the <media-gallery> opening tag, add ONE new attribute:
       *
       *   data-option-names="{{ product.options | join: ',' | downcase }}"
       *
       * It should sit alongside the existing data-* attributes, e.g.:
       *
       *   <media-gallery
       *     id="MediaGallery-{{ section.id }}"
       *     ...existing attributes...
       *     data-option-names="{{ product.options | join: ',' | downcase }}"
       *   >
       *
       * ── CHANGE B: snippets/product-thumbnail.liquid ──────────────────────
       *
       * On the outer <div> of each media slide, add ONE new attribute:
       *
       *   data-media-alt="{{ media.alt | escape }}"
       *
       * It sits alongside data-media-id, data-media-type, data-media-index:
       *
       *   <div
       *     role="listitem"
       *     class="product__media-item swiper-slide"
       *     data-media-id="{{ media.id }}"
       *     data-media-type="{{ media.media_type }}"
       *     data-media-index="{{ position }}"
       *     data-media-alt="{{ media.alt | escape }}"     ← ADD THIS
       *     data-src="..."
       *     ...
       *   >
       *
       * ── CHANGE C: snippets/product-media-gallery.liquid (thumbnail strip) ─
       *
       * On the thumb <div> (class="product__thumbs-item swiper-slide"), add:
       *
       *   data-media-alt="{{ media.alt | escape }}"
       *
       * Example:
       *   <div
       *     class="product__thumbs-item swiper-slide"
       *     data-target="{{ media.id }}"
       *     data-media-position="{{ media_index }}"
       *     data-media-alt="{{ media.alt | escape }}"     ← ADD THIS
       *   >
       * ═══════════════════════════════════════════════════════════════════
       */

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}